// src/hooks/useYearbookState.js
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../services/api';
import useParentPhotos from './useParentPhotos';

export default function useYearbookState({ routeProjectId } = {}) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentUser, setCurrentUser] = useState(null);
  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const initialRouteProjectIdRef = useRef(routeProjectId);

  const [isPro, setIsPro] = useState(false);
  const [bookConfig, setBookConfig] = useState(null);
  const [limits, setLimits] = useState({
    photos: 0,
    memories: 0,
    personalNotes: 0,
    personalNoteChars: 0,
    schoolPhotos: 0,
    photoPool: 0,
    photoPoolRemaining: 0,
  });
  const [progress, setProgress] = useState({
    photos: 0,
    personalNotes: 0,
    memories: 0,
    schoolPhotos: 0,
    shippingDone: false,
  });
  const [sections, setSections] = useState([]);
  const [offer, setOffer] = useState(null);
  const [upgrade, setUpgrade] = useState({ available_offers: [] });

  const [coverOptions, setCoverOptions] = useState([]);
  const [letterText, setLetterText] = useState('');
  const [letterNotes, setLetterNotes] = useState([]);
  const [memories, setMemories] = useState([]);
  const [schoolPhotos, setSchoolPhotos] = useState([]);
  const [orderSummary, setOrderSummary] = useState(null);

  const activeChild = useMemo(
    () => children.find((child) => child.id === activeChildId),
    [children, activeChildId]
  );

  const handleBackendPhotosChange = useCallback((nextPhotos = []) => {
    const nextCount = Array.isArray(nextPhotos) ? nextPhotos.length : 0;

    setProgress((prev) => ({
      ...prev,
      // The parent-photos endpoint is the source of truth for the current count.
      // Use the exact count so deletes are reflected immediately as well as adds.
      photos: nextCount,
    }));
  }, []);

  const parentPhotos = useParentPhotos(activeChild?.project_id, {
    onBackendPhotosChange: handleBackendPhotosChange,
  });

  const applyDashboardState = useCallback((dashboardData = {}) => {
    const yearbookConfig = dashboardData.yearbook_config || {};
    if (yearbookConfig && dashboardData.deadline) {
      yearbookConfig.content_deadline = dashboardData.deadline;
    }
    setBookConfig(yearbookConfig);

    if (dashboardData.limits) {
      const tier = String(dashboardData.limits.tier || '').toLowerCase();
      setIsPro(tier === 'plus' || tier === 'pro');
      setLimits({
          photos: Number(
            dashboardData.limits.personal_photos ?? 0
          ),

          memories: Number(
            dashboardData.limits.memories ?? 0
          ),

          personalNotes: Number(
            dashboardData.limits.personal_notes ?? 0
          ),

          personalNoteChars: Number(
            dashboardData.limits.personal_note_chars ??
            dashboardData.limits.letter_chars ??
            dashboardData.limits.letter_characters ??
            0
          ),

          schoolPhotos: Number(
            dashboardData.limits.school_photos ?? 0
          ),

          photoPool: Number(
            dashboardData.limits.photo_pool ?? 0
          ),

          photoPoolRemaining: Number(
            dashboardData.limits.photo_pool_remaining ?? 0
          ),
        });
    }

    if (dashboardData.progress) {
      setProgress({
        photos: dashboardData.progress.personal_photos,
        personalNotes: dashboardData.progress.personal_notes_filled ?? 0,
        memories: dashboardData.progress.memories_filled,
        schoolPhotos: dashboardData.progress.school_photos_selected,
        shippingDone: dashboardData.progress.shipping_done,
      });
    }

    setSections(dashboardData.sections || []);
    setOffer(dashboardData.offer || null);
    setUpgrade({
      ...(dashboardData.upgrade || {}),
      available_offers: Array.isArray(dashboardData.upgrade?.available_offers)
        ? dashboardData.upgrade.available_offers
        : [],
    });
    setCoverOptions(dashboardData.cover_options || []);
    setOrderSummary(dashboardData.order_summary || null);
  }, []);

  const normalizeParentPhotos = useCallback((parentPhotosData) => {
    if (Array.isArray(parentPhotosData)) return parentPhotosData;
    if (Array.isArray(parentPhotosData?.photos)) return parentPhotosData.photos;
    if (Array.isArray(parentPhotosData?.items)) return parentPhotosData.items;
    return [];
  }, []);

  useEffect(() => {
    async function fetchUser() {
      try {
        const profile = await api.getMe();
        const user = {
          user_id: profile.user_id,
          name: profile.name,
          email: profile.email,
          isSchoolBuy: false,
        };

        const profileChildren = profile.children || [];
        const routeChild = findChildByProjectId(profileChildren, initialRouteProjectIdRef.current);
        const initialChild = routeChild || profileChildren[0];

        setCurrentUser(user);
        setChildren(profileChildren);

        if (initialChild) {
          setActiveChildId(initialChild.id);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError('Failed to load profile. Please log in again.');
        setIsLoading(false);
      }
    }

    fetchUser();
  }, []);

  useEffect(() => {
    if (!routeProjectId || children.length === 0) return;

    const routeChild = findChildByProjectId(children, routeProjectId);

    if (routeChild && routeChild.id !== activeChildId) {
      setActiveChildId(routeChild.id);
    }
  }, [activeChildId, children, routeProjectId]);

  const refreshProjectData = useCallback(
    async ({ showLoading = true } = {}) => {
      if (!activeChild?.project_id) {
        return { ok: false, error: new Error('No active project selected') };
      }

      if (showLoading) setIsLoading(true);
      setError(null);

      try {
        const [dashboardData, parentPhotosData, letterData, memoriesData, schoolPhotosData] =
          await Promise.all([
            api.getProjectDashboard(activeChild.project_id),
            api.getParentPhotos(activeChild.project_id),
            api.getLetters(activeChild.project_id),
            api.getMemories(activeChild.project_id),
            // api.getSchoolPhotos(activeChild.project_id),
          ]);

        applyDashboardState(dashboardData);

        parentPhotos.setBackendPhotosFromServer(normalizeParentPhotos(parentPhotosData));

        const normalizedLetters = Array.isArray(letterData) ? letterData : [];
        const normalizedMemories = Array.isArray(memoriesData) ? memoriesData : [];
        setLetterNotes(normalizedLetters);
        setLetterText(normalizedLetters[0]?.data?.text || '');
        setMemories(normalizedMemories);

        // Keep the UI counters tied to the content we just loaded. This avoids a
        // stale/cached dashboard count forcing the user to manually refresh.
        setProgress((prev) => ({
          ...prev,
          personalNotes: normalizedLetters.length,
          memories: normalizedMemories.filter((memory) =>
            String(memory?.data?.text ?? memory?.text ?? '').trim().length > 0
          ).length,
        }));
        // setSchoolPhotos(schoolPhotosData?.chapters || []);

        return { ok: true };
      } catch (err) {
        console.error('Failed to load project data:', err);
        setError('Failed to load yearbook data.');
        return { ok: false, error: err };
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [
      activeChild?.project_id,
      applyDashboardState,
      normalizeParentPhotos,
      parentPhotos.setBackendPhotosFromServer,
    ]
  );

  useEffect(() => {
    if (!activeChild?.project_id) return;
    refreshProjectData({ showLoading: true });
  }, [activeChild?.project_id, refreshProjectData]);

  const updateChild = useCallback(
    async (updates) => {
      setChildren((prev) =>
        prev.map((child) => (child.id === activeChildId ? { ...child, ...updates } : child))
      );

      try {
        await api.updateChild(activeChildId, updates);
        return { ok: true };
      } catch (err) {
        console.error('Failed to update child:', err);
        return { ok: false, error: err };
      }
    },
    [activeChildId]
  );

  // const addPhotos = parentPhotos.addPhotos;
  // const addPhoto = parentPhotos.addPhotos;
  // const removePhoto = parentPhotos.removePhoto;
  const removeUploadItem = parentPhotos.removeUploadItem;

  const addPhotos = useCallback(
    async (input) => {
      const result = await parentPhotos.addPhotos(input);

      if (result?.ok && activeChild?.project_id) {
        await refreshProjectData({
          showLoading: false,
        });
      }

      return result;
    },
    [
      activeChild?.project_id,
      parentPhotos.addPhotos,
      refreshProjectData,
    ]
  );

  const addPhoto = addPhotos;

  const removePhoto = useCallback(
    async (photoId) => {
      const result = await parentPhotos.removePhoto(photoId);

      if (result?.ok && activeChild?.project_id) {
        await refreshProjectData({
          showLoading: false,
        });
      }

      return result;
    },
    [
      activeChild?.project_id,
      parentPhotos.removePhoto,
      refreshProjectData,
    ]
  );

  const saveLetter = useCallback(
    async (text) => {
      if (!activeChild?.project_id) {
        return { ok: false, error: new Error('No active project selected') };
      }

      const normalizedText = typeof text === 'string' ? text : '';

      try {
        await api.saveLetter(activeChild.project_id, normalizedText);
        setLetterText(normalizedText);
        setProgress((prev) => ({
          ...prev,
          // saveLetter is the legacy single-note path; saving it should not
          // increment the count every time the same note is edited.
          personalNotes: normalizedText.trim().length > 0
            ? Math.max(letterNotes.length, 1)
            : letterNotes.length,
        }));
        return { ok: true };
      } catch (err) {
        console.error('Failed to save letter:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id, letterNotes.length]
  );

  const addLetter = useCallback(
    async (text) => {
      if (!activeChild?.project_id) {
        return { ok: false, error: new Error('No active project selected') };
      }

      const normalizedText = typeof text === 'string' ? text : '';

      try {
        const newLetter = await api.createLetter(activeChild.project_id, normalizedText);
        setLetterNotes((prev) => {
          const next = [...prev, newLetter];
          setProgress((progressPrev) => ({
            ...progressPrev,
            personalNotes: next.length,
          }));
          return next;
        });
        setLetterText((prev) => prev || normalizedText);
        return { ok: true, letter: newLetter };
      } catch (err) {
        console.error('Failed to add letter:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id]
  );

  const updateLetter = useCallback(
    async (letterId, text) => {
      if (!activeChild?.project_id || !letterId) {
        return { ok: false, error: new Error('No active project or note selected') };
      }

      const normalizedText = typeof text === 'string' ? text : '';

      try {
        const updatedLetter = await api.updateLetter(
          activeChild.project_id,
          letterId,
          normalizedText
        );

        setLetterNotes((prev) =>
          prev.map((letter) =>
            letter.id === letterId || letter.content_id === letterId
              ? {
                  ...letter,
                  ...updatedLetter,
                  data: {
                    ...(letter.data || {}),
                    ...(updatedLetter?.data || {}),
                    text: normalizedText,
                  },
                }
              : letter
          )
        );

        setLetterText((prev) => prev || normalizedText);
        return { ok: true, letter: updatedLetter };
      } catch (err) {
        console.error('Failed to update letter:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id]
  );

  const deleteLetter = useCallback(
    async (letterId) => {
      if (!activeChild?.project_id || !letterId) {
        return { ok: false, error: new Error('No active project or note selected') };
      }

      try {
        await api.deleteLetter(activeChild.project_id, letterId);

        setLetterNotes((prev) => {
          const next = prev.filter(
            (letter) => letter.id !== letterId && letter.content_id !== letterId
          );

          setProgress((progressPrev) => ({
            ...progressPrev,
            personalNotes: next.length,
          }));

          setLetterText(next[0]?.data?.text || '');

          return next;
        });

        return { ok: true };
      } catch (err) {
        console.error('Failed to delete letter:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id]
  );

  const updateMemory = useCallback(
    async (id, value) => {
      if (!activeChild?.project_id || !id) {
        return { ok: false, error: new Error('No active project or memory selected') };
      }

      const previousMemory = memories.find((memory) => memory.id === id);
      const previousText = previousMemory?.data?.text || '';
      const nextText = typeof value === 'string' ? value : '';

      setMemories((prev) =>
        prev.map((memory) =>
          memory.id === id
            ? { ...memory, data: { ...memory.data, text: nextText } }
            : memory
        )
      );

      try {
        await api.updateMemory(activeChild.project_id, id, { value: nextText });

        const wasFilled = previousText.trim().length > 0;
        const isFilled = nextText.trim().length > 0;

        if (wasFilled !== isFilled) {
          setProgress((prev) => ({
            ...prev,
            memories: Math.max(0, (Number(prev.memories) || 0) + (isFilled ? 1 : -1)),
          }));
        }

        return { ok: true };
      } catch (err) {
        setMemories((prev) =>
          prev.map((memory) => (memory.id === id && previousMemory ? previousMemory : memory))
        );
        console.error('Failed to update memory:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id, memories]
  );

  const addCustomMemory = useCallback(
    async (title, value) => {
      if (!activeChild?.project_id) {
        return { ok: false, error: new Error('No active project selected') };
      }

      try {
        const newMemory = await api.createMemory(activeChild.project_id, {
          title,
          value,
          freeText: true,
        });

        setMemories((prev) => [...prev, newMemory]);
        setProgress((prev) => ({
          ...prev,
          memories: (Number(prev.memories) || 0) + 1,
        }));

        return { ok: true, memory: newMemory };
      } catch (err) {
        console.error('Failed to add custom memory:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id]
  );

  const toggleSchoolPhoto = useCallback(
    async (chapterId, photoId) => {
      let isNowSelected = false;

      setSchoolPhotos((prev) =>
        prev.map((chapter) => {
          if (chapter.id !== chapterId) return chapter;

          return {
            ...chapter,
            photos: chapter.photos.map((photo) => {
              if (photo.id === photoId) {
                isNowSelected = !photo.selected;
                return { ...photo, selected: isNowSelected };
              }

              return photo;
            }),
          };
        })
      );

      try {
        const payload = {
          select: isNowSelected ? [photoId] : [],
          deselect: isNowSelected ? [] : [photoId],
        };

        await api.updateSchoolPhotoSelections(activeChild.project_id, payload);
        return { ok: true, selected: isNowSelected };
      } catch (err) {
        console.error('Failed to save school photo selections:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id]
  );

  const updateCover = useCallback(
    async (coverStyle) => {
      try {
        await api.updateCover(activeChild.project_id, coverStyle);
        setChildren((prev) =>
          prev.map((child) =>
            child.id === activeChildId ? { ...child, cover_style: coverStyle } : child
          )
        );
        return { ok: true };
      } catch (err) {
        console.error('Failed to update cover style:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id, activeChildId]
  );

  const removeMemory = useCallback(
    async (id) => {
      if (!activeChild?.project_id || !id) {
        return { ok: false, error: new Error('No active project or memory selected') };
      }

      const previousMemories = memories;
      const removedMemory = memories.find((memory) => memory.id === id);

      setMemories((prev) => prev.filter((memory) => memory.id !== id));

      try {
        await api.deleteMemory(activeChild.project_id, id);

        if ((removedMemory?.data?.text || '').trim()) {
          setProgress((prev) => ({
            ...prev,
            memories: Math.max(0, (Number(prev.memories) || 0) - 1),
          }));
        }

        return { ok: true };
      } catch (err) {
        setMemories(previousMemories);
        console.error('Failed to delete memory:', err);
        return { ok: false, error: err };
      }
    },
    [activeChild?.project_id, memories]
  );

  return {
    isLoading,
    error,
    currentUser,
    children,
    activeChild,
    activeChildId,
    setActiveChildId,
    isPro,
    limits,
    bookConfig,
    progress,
    sections,
    offer,
    upgrade,
    upgradeOffers: upgrade.available_offers,
    refreshProjectData,

    photos: parentPhotos.backendPhotos,
    backendPhotos: parentPhotos.backendPhotos,

    uploadItems: parentPhotos.uploadItems,
    displayPhotos: parentPhotos.displayPhotos,

    letterText,
    letterNotes,
    memories,
    schoolPhotos,

    updateChild,
    addPhoto,
    addPhotos,
    removePhoto,
    removeUploadItem,
    saveLetter,
    addLetter,
    updateLetter,
    deleteLetter,
    updateMemory,
    addCustomMemory,
    toggleSchoolPhoto,
    updateCover,
    coverOptions,
    orderSummary,
    removeMemory,
  };
}

function findChildByProjectId(children, projectId) {
  if (!projectId) return null;
  return children.find((child) => child.project_id === projectId) || null;
}